import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Divider,
  IconButton,
  Button,
  Paper,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as Icon1 } from '../assets/images/popup1.svg';
import { ReactComponent as Icon2 } from '../assets/images/popup2.svg';
import { ReactComponent as Icon3 } from '../assets/images/popup3.svg';
import { ReactComponent as Icon4 } from '../assets/images/popup4.svg';

export default function HowItWorksPopup({ open, onClose }) {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            maxHeight: '85vh',
            maxWidth: '420px',
            display: 'flex',
            px: 1,
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default,
          },
        },
      }}
    >
      {/* Header (Fixed) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'transparent',
          zIndex: 1,
          px: 6,
          pt: 4,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Box>
            <Typography variant="h4" fontWeight={600} paragraph>
              Marriage Prediction Answers
            </Typography>
            <Typography
              variant="body1"
              fontSize={12}
              fontWeight={600}
              paragraph
              color="text.secondary"
            >
              Understand what our AI-Powered Vedic astrology system is capable
              of predicting in marriage timing.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 2,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <DialogContent dividers sx={{ width: '100%', px: 6, pb: 2, pt: 3 }}>
        {/* 1. Future Possibilities */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} ml={-3}>
            <Icon1 sx={{ color: '#9b5de5' }} fontSize="small" />
            <Typography variant="body1" fontWeight={600}>
              1. Future Possibilities
            </Typography>
          </Box>
          <Typography variant="h5" mb={1.5} pr={2}>
            You'll receive <strong>upto 3 most likely</strong> auspicious future
            favorable{' '}
            <strong>Marriage muhurates (Month–Year) combinations</strong> when
            marriage is possible.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              backgroundColor:
                theme.palette.mode == 'dark' ? '#1e1e1e' : '#fafafa',
              p: 2,
              mb: 1,
              borderRadius: '8px',
              border: 'none',
            }}
          >
            <Typography variant="body1" fontSize={12} color="text.secondary">
              These aren't random guesses - they’re calculated using:
              <ul style={{ paddingLeft: 16 }}>
                <li>Your Birth Chart (Janam Kundli)</li>
                <li>
                  Your Navamsha Chart (D-9, marriage-specific divisional chart)
                </li>
                <li>Current and upcoming Planetary Transits</li>
              </ul>
            </Typography>
          </Paper>
          <Typography
            variant="body1"
            fontSize={10}
            color="text.secondary"
            fontStyle="italic"
          >
            This layered approach ensures high accuracy and clarity.
          </Typography>
        </Box>

        <Divider sx={{ mt: 2, mb: 2 }} />

        {/* 2. Past Marriage Dates */}
        <Box>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={1}
            mb={1.5}
            ml={-3}
            mr={-3}
          >
            <Icon2 sx={{ color: '#9b5de5' }} fontSize="small" />
            <Typography variant="body1" fontWeight={600} width="100%">
              2. Past Marriage Dates{' '}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10 }}
              >
                (Optional for Married Users)
              </Typography>
            </Typography>
          </Box>
          <Typography variant="h5" mb={1.5} pr={2}>
            By default, predictions are designed for those{' '}
            <strong>who are unmarried.</strong>
          </Typography>
          <Typography variant="h5" mb={1}>
            But if you are already married, you can{' '}
            <strong>test the accuracy</strong> of our system:
            <ul style={{ marginTop: 0, marginBottom: 6, paddingLeft: 16 }}>
              <li>
                You'll get two immediate previous dates connected to marriage.
              </li>
              <li>
                If you want to go further back, you can request more in sets of
                two dates each time.{' '}
              </li>
              <li>Every request costs 10 Tokens.</li>
            </ul>
          </Typography>
          <Typography
            variant="body1"
            fontSize={10}
            color="text.secondary"
            fontStyle="italic"
          >
            This way, you can validate how well our system works for you.
          </Typography>
        </Box>

        <Divider sx={{ mt: 2, mb: 2 }} />

        {/* 3. Predictions for Unmarried */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} ml={-3}>
            <Icon3 sx={{ color: '#9b5de5' }} fontSize="small" />
            <Typography variant="body1" fontWeight={600}>
              3. Predictions Primarily for Unmarried
            </Typography>
          </Box>
          <Typography variant="h5" mb={1.5} pr={2}>
            Our main focus is predicting marriage timing for{' '}
            <strong>unmarried individuals.</strong>
          </Typography>
          <Typography variant="h5" pr={2}>
            However, married users still benefit by testing the system with
            their past marriage events.
          </Typography>
        </Box>

        <Divider sx={{ mt: 2, mb: 2 }} />

        {/* 4. Powered by AI */}
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} ml={-3}>
            <Icon4 sx={{ color: '#0077b6' }} fontSize="small" />
            <Typography variant="body1" fontWeight={600}>
              4. Powered by AI
            </Typography>
          </Box>
          <Typography variant="h5" mb={1.5} pr={2}>
            Our AI doesn't just give dates - it reads your chart in depth.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              backgroundColor:
                theme.palette.mode == 'dark' ? '#1e1e1e' : '#fafafa',
              p: 2,
              borderRadius: '8px',
              border: 'none',
            }}
          >
            <Typography variant="body1" fontSize={12} color="text.secondary">
              It scans <strong>8 different layers</strong> of your horoscope to
              identify <em>Marriage Yogas</em> - astrological combinations that
              signal marriage.
            </Typography>
          </Paper>
        </Box>
      </DialogContent>

      {/* Footer Button */}
      {/* <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Got it
        </Button>
      </DialogActions> */}
    </Dialog>
  );
}
